import { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import api, { money, formatApiErrorDetail, formatDateRange } from "../lib/api";
import { MODULES } from "../lib/modules";
import FilterBanner from "../components/FilterBanner";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Building2, DoorOpen, Plus, Pencil, RefreshCw, Sparkles, TrendingDown, TrendingUp, Minus, User, CalendarRange, LogOut, Trash2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import DocActions from "../components/DocActions";
import RenewalsPanel from "./LeaseRenewals";

const leaseStatusTone = { Active: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300",
  Pending: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  Expired: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300" };

function MarketComparison({ current, market }) {
  if (!market) return <span className="text-xs text-muted-foreground italic">No market estimate yet</span>;
  if (!current) {
    return (
      <div className="space-y-1 min-w-[180px]" data-testid="market-badge">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground"><Minus className="w-3.5 h-3.5" /> Vacant</div>
        <p className="text-[10px] text-muted-foreground">Market rent {money(market)}/mo</p>
      </div>
    );
  }
  const pct = Math.round(((current - market) / market) * 100);
  const under = pct <= -5, over = pct >= 5;
  const tone = under ? "text-red-600" : over ? "text-emerald-600" : "text-amber-600";
  const Icon = under ? TrendingDown : over ? TrendingUp : Minus;
  const label = under ? `${Math.abs(pct)}% under market` : over ? `${pct}% above market` : "At market";
  const fillPct = Math.min(100, market ? (current / market) * 100 : 0);
  const barColor = under ? "bg-red-500" : over ? "bg-emerald-500" : "bg-amber-500";
  return (
    <div className="space-y-1.5 min-w-[180px]">
      <div className={`flex items-center gap-1.5 text-xs font-semibold ${tone}`} data-testid="market-badge">
        <Icon className="w-3.5 h-3.5" /> {label}
      </div>
      <div className="relative h-2 rounded-full bg-muted overflow-hidden">
        <div className={`absolute inset-y-0 left-0 ${barColor} rounded-full transition-all`} style={{ width: `${fillPct}%` }} />
        <div className="absolute inset-y-0 w-0.5 bg-foreground/60" style={{ left: "100%", transform: "translateX(-2px)" }} title="Market rent" />
      </div>
      <div className="flex justify-between text-[10px] text-muted-foreground">
        <span>Current {money(current)}</span>
        <span>Market {money(market)}</span>
      </div>
    </div>
  );
}

export default function LeasesView() {
  const { user, canWrite } = useAuth();
  const [sp, setSp] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [leases, setLeases] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshingId, setRefreshingId] = useState(null);
  const [modal, setModal] = useState(null); // {type, editing, form, prefill}
  const [tab, setTab] = useState(sp.get("tab") === "renewals" ? "renewals" : "units");
  const [renewalCount, setRenewalCount] = useState(0);
  useEffect(() => {
    api.get("/lease-renewals").then((r) => setRenewalCount((r.data || []).filter((t) => t.status === "Open").length)).catch(() => {});
  }, []);
  useEffect(() => { const t = sp.get("tab"); if (t === "renewals" || t === "units") setTab(t); }, [sp]);
  const selectTab = (t) => {
    setTab(t);
    const next = new URLSearchParams(sp);
    if (t === "units") next.delete("tab"); else next.set("tab", t);
    setSp(next, { replace: true });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, u, l, t] = await Promise.all([
        api.get("/properties"), api.get("/units"), api.get("/leases"), api.get("/tenants"),
      ]);
      setProperties(p.data); setUnits(u.data); setLeases(l.data); setTenants(t.data);
    } catch (e) {
      toast.error("Failed to load lease data");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const normUnit = (s) => String(s || "").trim().toLowerCase().replace(/^unit\s+/, "");
  const leaseForUnit = (unit) => leases.find((l) =>
    (l.property_id === unit.property_id) && normUnit(l.unit) === normUnit(unit.unit_number)
  ) || leases.find((l) => l.property_id === unit.property_id && l.tenant_name && l.tenant_name === unit.tenant_name);

  const currentRent = (unit, lease) => Number(lease?.monthly_rent || unit.rent_amount || 0);

  const refreshMarket = async (unit) => {
    const prop = properties.find((p) => p.id === unit.property_id);
    if (!prop?.address) { toast.error("This unit's property needs an address first"); return; }
    setRefreshingId(unit.id);
    try {
      const { data } = await api.get("/unit/rent-estimate", { params: { address: prop.address } });
      await api.put(`/units/${unit.id}`, {
        market_rent: data.rent, market_rent_low: data.rent_low,
        market_rent_high: data.rent_high, market_rent_updated: new Date().toISOString(),
      });
      toast.success(`Market rent updated: ${money(data.rent)}/mo`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setRefreshingId(null); }
  };

  const openUnit = (editing, prefill = {}) => setModal({ type: "units", editing, form: editing ? { ...editing } : { ...prefill }, lockField: editing ? null : (prefill.property_name ? "property_name" : null) });
  const openLease = (editing, prefill = {}) => setModal({ type: "leases", editing, form: editing ? { ...editing } : { status: "Active", ...prefill } });

  const moveOut = async (lease) => {
    if (!window.confirm(`End ${lease.tenant_name || "this"} lease and mark the unit available?`)) return;
    try { await api.post(`/leases/${lease.id}/end`); toast.success("Lease ended — unit is now available"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const deleteUnit = async (unit) => {
    const lease = leaseForUnit(unit);
    const who = unit.tenant_name ? ` (${unit.tenant_name})` : "";
    const msg = lease
      ? `Delete unit "${unit.unit_number}"${who} and its lease? This cannot be undone.`
      : `Delete unit "${unit.unit_number}"${who}? This cannot be undone.`;
    if (!window.confirm(msg)) return;
    try {
      if (lease) await api.delete(`/leases/${lease.id}`);
      await api.delete(`/units/${unit.id}`);
      toast.success("Unit deleted");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not delete unit"); }
  };

  const deleteLease = async (lease) => {
    const who = lease.tenant_name ? ` for ${lease.tenant_name}` : "";
    if (!window.confirm(`Delete this lease${who}? This cannot be undone.`)) return;
    try {
      await api.delete(`/leases/${lease.id}`);
      toast.success("Lease deleted");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not delete lease"); }
  };

  const byProperty = properties.map((p) => ({
    property: p,
    units: units.filter((u) => u.property_id === p.id),
  }));
  const orphanUnits = units.filter((u) => !properties.some((p) => p.id === u.property_id));
  if (orphanUnits.length) byProperty.push({ property: { id: "", name: "Unassigned", address: "" }, units: orphanUnits });

  // Leases not linked to any rendered unit (e.g. duplicates with no property/unit) — surface them so they can be deleted.
  const shownLeaseIds = new Set(units.map((u) => leaseForUnit(u)?.id).filter(Boolean));
  const orphanLeases = leases.filter((l) => !shownLeaseIds.has(l.id));

  const expiringDays = sp.get("expiring") ? Number(sp.get("expiring")) : null;
  const expiringFilter = expiringDays !== null && !isNaN(expiringDays);
  const isExpiring = (unit) => {
    const lease = leaseForUnit(unit);
    const end = lease?.end_date;
    if (!end) return false;
    const now = new Date(); now.setHours(0, 0, 0, 0);
    const d = new Date(String(end).slice(0, 10));
    return !isNaN(d.getTime()) && d <= new Date(now.getTime() + expiringDays * 86400000);
  };
  const groups = expiringFilter
    ? byProperty.map((g) => ({ ...g, units: g.units.filter(isExpiring) })).filter((g) => g.units.length > 0)
    : byProperty;
  const expiringCount = expiringFilter ? groups.reduce((n, g) => n + g.units.length, 0) : 0;

  const leaseExpiryBadge = (lease) => {
    if (!lease?.end_date || lease.status === "Expired") return null;
    const now = new Date(); now.setHours(0, 0, 0, 0);
    const d = new Date(String(lease.end_date).slice(0, 10));
    if (isNaN(d.getTime())) return null;
    const days = Math.round((d - now) / 86400000);
    if (days < 0) return <Badge className="rounded-sm font-medium bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">Expired</Badge>;
    if (days > 45) return null;
    const txt = days === 0 ? "Expires today" : days === 1 ? "Expires tomorrow" : `Expires in ${days} days`;
    return <Badge className="rounded-sm font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">{txt}</Badge>;
  };

  if (loading) return <div className="p-8 text-muted-foreground">Loading leases…</div>;

  return (
    <div className="space-y-6" data-testid="page-leases">
      {expiringFilter && <FilterBanner label={sp.get("label") || `Leases expiring within ${expiringDays} days`} count={expiringCount} onClear={() => setSp({}, { replace: true })} />}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">Portfolio</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1 flex items-center gap-2"><DoorOpen className="w-7 h-7 text-primary" /> Leases & Units</h1>
          <p className="text-sm text-muted-foreground mt-1">Every unit with its lease, tenant, and current vs. market rent — plus upcoming renewals.</p>
        </div>
        {canWrite && tab === "units" && (
          <Button className="rounded-xl" onClick={() => openLease(null)} data-testid="add-lease-btn"><Plus className="w-4 h-4 mr-1" /> Add Lease</Button>
        )}
      </div>

      <div className="flex gap-2 border-b border-border">
        {[["units", "Units & Leases"], ["renewals", "Renewals"]].map(([k, l]) => (
          <button key={k} onClick={() => selectTab(k)} data-testid={`leases-tab-${k}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors inline-flex items-center gap-2 ${tab === k ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            {l}
            {k === "renewals" && renewalCount > 0 && (
              <span data-testid="renewals-tab-count" className="min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-semibold flex items-center justify-center">{renewalCount > 99 ? "99+" : renewalCount}</span>
            )}
          </button>
        ))}
      </div>

      {tab === "renewals" ? <RenewalsPanel /> : (
      <div className="space-y-6">
      {expiringFilter && groups.length === 0 && (
        <div className="rounded-2xl border border-border bg-card p-10 text-center text-muted-foreground" data-testid="leases-empty-expiring">No leases expiring within {expiringDays} days.</div>
      )}
      {groups.map(({ property, units: pu }) => {
        const totalCurrent = pu.reduce((s, u) => s + currentRent(u, leaseForUnit(u)), 0);
        const totalMarket = pu.reduce((s, u) => s + Number(u.market_rent || 0), 0);
        const withMarket = pu.filter((u) => Number(u.market_rent || 0) > 0).length;
        const marketLabel = withMarket === 0 ? "—"
          : withMarket === pu.length ? money(totalMarket)
          : `${money(totalMarket)} (${withMarket}/${pu.length})`;
        return (
          <section key={property.id || "unassigned"} className="rounded-2xl border border-border bg-card overflow-hidden" data-testid={`property-group-${property.id || "none"}`}>
            <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-border bg-muted/30">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><Building2 className="w-5 h-5" /></div>
                <div>
                  <h2 className="font-heading font-semibold">{property.name}</h2>
                  <p className="text-xs text-muted-foreground">{property.address || "—"} · {pu.length} unit{pu.length !== 1 ? "s" : ""}</p>
                </div>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <div className="text-right"><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Current / Market</p>
                  <p className="font-mono font-semibold">{money(totalCurrent)} <span className="text-muted-foreground">/</span> {marketLabel}</p></div>
                {canWrite && property.id && (
                  <Button variant="outline" size="sm" className="rounded-xl" onClick={() => openUnit(null, { property_name: property.name, property_id: property.id })} data-testid={`add-unit-${property.id}`}><Plus className="w-3.5 h-3.5 mr-1" /> Unit</Button>
                )}
              </div>
            </div>

            {pu.length === 0 ? (
              <p className="p-6 text-sm text-muted-foreground text-center">No units yet. {canWrite && property.id && <button className="text-primary hover:underline" onClick={() => openUnit(null, { property_name: property.name, property_id: property.id })}>Add the first unit</button>}</p>
            ) : (
              <div className="divide-y divide-border">
                {pu.map((unit) => {
                  const lease = leaseForUnit(unit);
                  const cur = currentRent(unit, lease);
                  return (
                    <div key={unit.id} className="p-4 grid grid-cols-1 lg:grid-cols-12 gap-4 items-center hover:bg-muted/30 transition-colors" data-testid={`unit-row-${unit.id}`}>
                      {/* Unit + tenant */}
                      <div className="lg:col-span-3">
                        <div className="flex items-center gap-2">
                          <DoorOpen className="w-4 h-4 text-muted-foreground" />
                          <p className="font-semibold">{/unit/i.test(String(unit.unit_number)) ? unit.unit_number : `Unit ${unit.unit_number}`}</p>
                          <Badge className={`rounded-sm font-normal ${unit.status === "Occupied" ? leaseStatusTone.Active : "bg-muted text-muted-foreground"}`}>{unit.status || "—"}</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><User className="w-3 h-3" /> {lease?.tenant_name || unit.tenant_name || "Vacant"}</p>
                      </div>

                      {/* Rent + market comparison */}
                      <div className="lg:col-span-4 flex items-center gap-4">
                        <div className="min-w-[80px]">
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Current Rent</p>
                          <p className="font-mono font-bold">{money(cur)}</p>
                        </div>
                        <MarketComparison current={cur} market={Number(unit.market_rent || 0)} />
                      </div>

                      {/* Lease dates + doc */}
                      <div className="lg:col-span-3">
                        {lease ? (
                          <>
                            <p className="text-xs text-muted-foreground flex items-center gap-1.5 flex-wrap whitespace-nowrap">
                              <CalendarRange className="w-3 h-3 shrink-0" /> {formatDateRange(lease.start_date, lease.end_date)}
                              {leaseExpiryBadge(lease)}
                            </p>
                            <div className="mt-1.5"><DocActions moduleKey="leases" record={lease} canWrite={canWrite} onChange={load} /></div>
                          </>
                        ) : (
                          <span className="text-xs text-muted-foreground italic">No lease linked</span>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="lg:col-span-2 flex lg:justify-end items-center gap-2 flex-wrap">
                        {canWrite && (
                          <Button variant="outline" size="sm" className="rounded-lg" onClick={() => refreshMarket(unit)} disabled={refreshingId === unit.id} data-testid={`refresh-market-${unit.id}`} title="Refresh market rent">
                            {refreshingId === unit.id ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                            <span className="ml-1 hidden xl:inline">Market</span>
                          </Button>
                        )}
                        {canWrite && (
                          <button onClick={() => openUnit(unit)} data-testid={`edit-unit-${unit.id}`} className="text-muted-foreground hover:text-primary p-1.5"><Pencil className="w-4 h-4" /></button>
                        )}
                        {canWrite && (
                          lease
                            ? <button onClick={() => openLease(lease)} data-testid={`edit-lease-${unit.id}`} className="text-xs text-primary hover:underline">Edit lease</button>
                            : <button onClick={() => openLease(null, { property_name: property.name, property_id: property.id, unit: unit.unit_number, tenant_name: unit.tenant_name, monthly_rent: unit.rent_amount })} data-testid={`add-lease-${unit.id}`} className="text-xs text-primary hover:underline">+ Lease</button>
                        )}
                        {canWrite && lease && lease.status === "Active" && (
                          <button onClick={() => moveOut(lease)} data-testid={`moveout-${unit.id}`} className="text-xs text-red-600 hover:underline flex items-center gap-1"><LogOut className="w-3 h-3" /> Move Out</button>
                        )}
                        {canWrite && (
                          <button onClick={() => deleteUnit(unit)} data-testid={`delete-unit-${unit.id}`} className="text-muted-foreground hover:text-destructive p-1.5" title="Delete this unit"><Trash2 className="w-4 h-4" /></button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        );
      })}

      {!expiringFilter && orphanLeases.length > 0 && (
        <section className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="unassigned-leases-group">
          <div className="flex items-center gap-3 p-4 border-b border-border bg-amber-50 dark:bg-amber-900/10">
            <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 flex items-center justify-center"><User className="w-5 h-5" /></div>
            <div>
              <h2 className="font-heading font-semibold">Unassigned & Duplicate Leases</h2>
              <p className="text-xs text-muted-foreground">{orphanLeases.length} lease{orphanLeases.length !== 1 ? "s" : ""} not shown against a unit above (unlinked to a property/unit, or a duplicate). Review and delete as needed.</p>
            </div>
          </div>
          <div className="divide-y divide-border">
            {orphanLeases.map((lease) => (
              <div key={lease.id} className="p-4 grid grid-cols-1 lg:grid-cols-12 gap-4 items-center hover:bg-muted/30 transition-colors" data-testid={`orphan-lease-row-${lease.id}`}>
                <div className="lg:col-span-4">
                  <p className="text-sm font-semibold flex items-center gap-1.5"><User className="w-3.5 h-3.5 text-muted-foreground" /> {lease.tenant_name || "Unknown tenant"}</p>
                  <p className="text-xs text-muted-foreground mt-1">{lease.property_name ? lease.property_name : "No property"}{lease.unit ? ` · Unit ${lease.unit}` : ""}</p>
                </div>
                <div className="lg:col-span-3">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Rent</p>
                  <p className="font-mono font-bold">{money(Number(lease.monthly_rent || 0))}</p>
                </div>
                <div className="lg:col-span-3">
                  {lease.start_date || lease.end_date ? (
                    <p className="text-xs text-muted-foreground flex items-center gap-1.5 whitespace-nowrap"><CalendarRange className="w-3 h-3 shrink-0" /> {formatDateRange(lease.start_date, lease.end_date)}</p>
                  ) : <span className="text-xs text-muted-foreground italic">No dates</span>}
                  <div className="mt-1.5"><DocActions moduleKey="leases" record={lease} canWrite={canWrite} onChange={load} /></div>
                </div>
                <div className="lg:col-span-2 flex lg:justify-end items-center gap-2 flex-wrap">
                  {canWrite && (
                    <button onClick={() => openLease(lease)} data-testid={`edit-orphan-lease-${lease.id}`} className="text-xs text-primary hover:underline">Edit</button>
                  )}
                  {canWrite && (
                    <button onClick={() => deleteLease(lease)} data-testid={`delete-orphan-lease-${lease.id}`} className="text-muted-foreground hover:text-destructive p-1.5" title="Delete this lease"><Trash2 className="w-4 h-4" /></button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      </div>
      )}

      {modal && (
        <RecordDialog
          modal={modal} setModal={setModal} tenants={tenants} properties={properties} units={units}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}

function RecordDialog({ modal, setModal, tenants, properties, units = [], onSaved }) {
  const cfg = MODULES[modal.type];
  const [form, setForm] = useState(modal.form || {});
  const [saving, setSaving] = useState(false);
  const refs = { tenants, properties };
  const propertyUnits = units.filter((u) => u.property_id === form.property_id);

  const setField = (f, value) => {
    setForm((prev) => {
      const next = { ...prev, [f.key]: value };
      if (f.type === "ref") {
        const opt = (refs[f.ref] || []).find((o) => o[f.refLabel] === value);
        if (opt) next[f.setId] = opt.id;
        if (f.key === "property_name") next.unit = "";
      }
      return next;
    });
  };

  const pickLeaseUnit = (value) => {
    setForm((prev) => {
      const u = units.find((x) => x.property_id === prev.property_id && x.unit_number === value);
      const next = { ...prev, unit: value };
      if (u && !prev.monthly_rent && u.rent_amount) next.monthly_rent = u.rent_amount;
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      if (modal.editing) await api.put(`/${modal.type}/${modal.editing.id}`, form);
      else await api.post(`/${modal.type}`, form);
      toast.success(`${cfg.singular} ${modal.editing ? "updated" : "created"}`);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && setModal(null)}>
      <DialogContent className="rounded-xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={`${modal.type}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-heading">{modal.editing ? "Edit" : "Add"} {cfg.singular}</DialogTitle>
          <DialogDescription>{modal.editing ? "Update the" : "Create a new"} {cfg.singular.toLowerCase()} record.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
          {cfg.fields.map((f) => (
            <div key={f.key} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">{f.label}{f.required && " *"}</label>
              {f.type === "textarea" ? (
                <Textarea data-testid={`field-${f.key}`} value={form[f.key] || ""} onChange={(e) => setField(f, e.target.value)} className="rounded-xl" />
              ) : (modal.type === "leases" && f.key === "unit" && propertyUnits.length > 0) ? (
                <Select value={form.unit || ""} onValueChange={pickLeaseUnit}>
                  <SelectTrigger className="rounded-xl" data-testid="field-unit"><SelectValue placeholder={form.property_name ? "Select a unit..." : "Select a property first"} /></SelectTrigger>
                  <SelectContent>{propertyUnits.map((u) => <SelectItem key={u.id} value={u.unit_number}>{u.unit_number}{u.tenant_name ? ` · ${u.tenant_name}` : " · vacant"}</SelectItem>)}</SelectContent>
                </Select>
              ) : f.type === "select" ? (
                <Select value={form[f.key] || ""} onValueChange={(v) => setField(f, v)}>
                  <SelectTrigger className="rounded-xl" data-testid={`field-${f.key}`}><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>{f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              ) : f.type === "ref" ? (
                <Select value={form[f.key] || ""} onValueChange={(v) => setField(f, v)} disabled={modal.lockField === f.key}>
                  <SelectTrigger className="rounded-xl" data-testid={`field-${f.key}`}><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>{(refs[f.ref] || []).map((o) => <SelectItem key={o.id} value={o[f.refLabel]}>{o[f.refLabel]}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <Input
                  data-testid={`field-${f.key}`}
                  type={f.type === "number" || f.type === "money" ? "number" : f.type === "date" ? "date" : "text"}
                  value={form[f.key] ?? ""}
                  onChange={(e) => setField(f, f.type === "number" || f.type === "money" ? Number(e.target.value) : e.target.value)}
                  className="rounded-xl"
                />
              )}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-xl" onClick={() => setModal(null)}>Cancel</Button>
          <Button className="rounded-xl" onClick={save} disabled={saving} data-testid="save-button">{saving ? "Saving..." : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
